import { forwardRef } from "react";
import { Box, TextField } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { RADIUS } from "../../../theme/mixins";

interface StyledTextAreaProps extends Omit<TextFieldProps, "multiline"> {
  rows?: number;
  mode?: "light" | "dark";
}

const CustomTextArea = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "mode",
})<{ mode?: "light" | "dark" }>(({ theme, mode }) => ({
  borderRadius: RADIUS.input,
  "& .MuiOutlinedInput-root": {
    borderRadius: RADIUS.input,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    "& fieldset": {
      border:
        mode === "dark"
          ? "1px solid transparent"
          : `1px solid ${theme.palette.border.strong}`,
    },
    "&:hover fieldset": { borderColor: theme.palette.border.grey },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
    "& textarea": { color: theme.palette.text.primary },
    "& textarea::placeholder": {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
}));

const StyledTextArea = forwardRef<HTMLTextAreaElement, StyledTextAreaProps>(
  ({ rows = 3, mode = "dark", label, sx, ...props }, ref) => {
    return (
      <Box sx={{ width: "100%" }}>
        {label !== undefined && label !== "" && (
          <Box
            component="label"
            sx={{
              display: "block",
              fontSize: "13px",
              fontWeight: 600,
              marginBottom: "6px",
              color: (theme) =>
                mode === "dark"
                  ? alpha(theme.palette.common.white, 0.82)
                  : theme.palette.text.primary,
            }}
          >
            {label}
          </Box>
        )}
        <CustomTextArea
          {...props}
          mode={mode}
          multiline
          rows={rows}
          inputRef={ref}
          fullWidth
          sx={sx}
        />
      </Box>
    );
  }
);

StyledTextArea.displayName = "StyledTextArea";

export default StyledTextArea;
